import { Star, BadgeCheck, Quote, MapPin } from "lucide-react";
import { Reveal, SectionHead } from "../components/ui-bits";
import { useApp } from "../context/AppContext";

export default function Testimonials() {
  const { config } = useApp();
  const items = config.testimonials || [];
  if (!items.length) return null;

  return (
    <section className="light-section relative py-20 lg:py-28" data-testid="testimonials-section">
      <div className="dot-grid-light pointer-events-none absolute inset-0" />
      <div className="container-x relative">
        <SectionHead
          eyebrow="Customer Reviews"
          light
          title={<>What Car Owners Say.</>}
          sub="Real words from customers whose cars CARZA cares for."
          align="center"
        />
        <div className="mt-12 overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]" data-testid="testimonials-marquee">
          <div className="marquee-track" style={{ animationDuration: "90s" }}>
            {[0, 1].map((copy) => (
              <div key={copy} className="flex gap-5 pr-5" aria-hidden={copy === 1 ? "true" : undefined}>
                {items.map((t, i) => {
                  const featured = copy === 0 && i === 0;
                  const tid = (s) => (copy === 0 ? { "data-testid": `testimonial-${s}-${i}` } : {});
                  return (
                    <div
                      key={`${copy}-${t.name}-${i}`}
                      className={`group relative flex h-full w-[320px] shrink-0 flex-col gap-4 overflow-hidden rounded-3xl p-6 transition-[transform,box-shadow] duration-300 hover:-translate-y-1.5 sm:w-[360px] ${
                        featured
                          ? "bg-gradient-to-br from-[#241b45] to-[#3b2a75] text-paper shadow-[0_20px_50px_-20px_rgba(109,61,245,0.55)]"
                          : "card-light shadow-[0_10px_30px_-18px_rgba(30,20,60,0.25)] hover:shadow-[0_24px_50px_-24px_rgba(109,61,245,0.45)]"
                      }`}
                      {...tid(`card-${i}`)}
                    >
                      <Quote size={44} strokeWidth={1.5} className={`pointer-events-none absolute -top-1 right-4 ${featured ? "text-white/10" : "text-carza/10"}`} />

                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <span
                            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl font-display text-sm font-bold ${
                              featured ? "bg-white/15 text-white" : "bg-gradient-to-br from-carza to-[#9b6dff] text-white"
                            }`}
                            {...tid(`avatar-${i}`)}
                          >
                            {(t.name || "?").trim().charAt(0).toUpperCase()}
                          </span>
                          <div>
                            <p className={`text-[15px] font-semibold ${featured ? "text-white" : "text-nighttext"}`} {...tid(`name-${i}`)}>{t.name}</p>
                            {t.city && (
                              <p className={`flex items-center gap-1 text-[11px] ${featured ? "text-white/60" : "text-mist-2"}`}>
                                <MapPin size={10} /> {t.city}
                              </p>
                            )}
                          </div>
                        </div>
                        {t.verified ? (
                          <span className="flex shrink-0 items-center gap-1 rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-medium text-emerald-600" {...tid(`verified-${i}`)}>
                            <BadgeCheck size={11} /> Verified
                          </span>
                        ) : null}
                      </div>

                      <div className={`flex w-fit items-center gap-1 rounded-full px-2.5 py-1 ${featured ? "bg-white/10" : "bg-amber-500/10"}`} {...tid(`stars-${i}`)}>
                        {Array.from({ length: 5 }).map((_, s) => (
                          <Star
                            key={s}
                            size={12}
                            className={
                              s < Math.min(5, Math.max(1, t.rating || 5))
                                ? "fill-amber-400 text-amber-400"
                                : featured
                                  ? "fill-white/20 text-white/20"
                                  : "fill-amber-200 text-amber-200"
                            }
                          />
                        ))}
                      </div>

                      <p className={`flex-1 text-[13.5px] leading-relaxed ${featured ? "text-white/85" : "text-[#3a3350]"}`} {...tid(`text-${i}`)}>
                        “{t.text}”
                      </p>

                      {t.service && (
                        <span
                          className={`self-start rounded-full border px-3 py-1 text-[11px] font-medium ${
                            featured ? "border-white/20 bg-white/10 text-white/90" : "border-carza/20 bg-carza/[0.06] text-carza"
                          }`}
                          {...tid(`service-${i}`)}
                        >
                          {t.service}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
